import { LeaInformationClient } from 'src/shared/services/api.service';
import { BehaviorSubject, Observable, tap, shareReplay, finalize } from 'rxjs';
import { Injectable } from '@angular/core';
import { LocalStorageService } from 'src/shared/services/local-storage.service';

@Injectable({
  providedIn: 'root',
})
export class LeaCodeService {
  private leaCodeIdSubject = new BehaviorSubject<number | null>(null);
  private readonly leaCodeId$ = this.leaCodeIdSubject.asObservable();
  private fetchOnce$?: Observable<number | null>;
  private leaCodeRequestVersion = 0;

  private leaEdOrgIdSubject = new BehaviorSubject<string | null>(null);
  private readonly leaEdOrgId$ = this.leaEdOrgIdSubject.asObservable();

  constructor(
    private readonly leaInformationClient: LeaInformationClient,
    private readonly localStorageService: LocalStorageService,
  ) {}

  getCurrentLeaCodeId(): Observable<number | null> {
    // If we don't yet have a value, kick off a one-time fetch in the background
    if (this.leaCodeIdSubject.value === null && !this.fetchOnce$) {
      const requestVersion = this.leaCodeRequestVersion;
      this.fetchOnce$ = this.leaInformationClient.getCurrentUserLeaCodeId().pipe(
        tap((id: number | null) => {
          if (requestVersion === this.leaCodeRequestVersion) {
            this.setCurrentLeaCodeId(id);
          }
        }),
        shareReplay(1),
        finalize(() => {
          // Allow subsequent fetch attempts (value check guards refetches)
          this.fetchOnce$ = undefined;
        }),
      );
      // Trigger fetch without forcing consumers to subscribe to a separate stream
      this.fetchOnce$.subscribe({ error: () => {} });
    }
    // Always return the live stream so consumers receive future updates
    return this.leaCodeId$;
  }

  get currentLeaCodeId(): number | null {
    return this.leaCodeIdSubject.value;
  }

  setCurrentLeaCodeId(leaCodeId: number | null) {
    // An explicit selection must win over a slower in-flight fetch of the user's default LEA.
    this.leaCodeRequestVersion++;
    this.leaCodeIdSubject.next(leaCodeId);
    if (leaCodeId == null) {
      this.localStorageService.remove('currentLeaCodeId');
    } else {
      this.localStorageService.set('currentLeaCodeId', String(leaCodeId));
    }
  }

  getCurrentLeaEdOrgId(): Observable<string | null> {
    return this.leaEdOrgId$;
  }

  setCurrentLeaEdOrgId(leaEdOrgId: string | null) {
    this.leaEdOrgIdSubject.next(leaEdOrgId);
    if (leaEdOrgId == null) {
      this.localStorageService.remove('currentLeaEdOrgId');
    } else {
      this.localStorageService.set('currentLeaEdOrgId', leaEdOrgId);
    }
  }

  resetLeaContext() {
    this.leaCodeRequestVersion++;
    this.fetchOnce$ = undefined;
    this.setCurrentLeaCodeId(null);
    this.setCurrentLeaEdOrgId(null);
  }
}
